import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { ThemeProvider } from "@/contexts/ThemeContext";

import User from "@/pages/ticket/user/page";
import Pin from "@/pages/ticket/pin/page";
import Home from "@/pages/home/page";
import Vehicles from "@/pages/vehicles/page";
import Checkout from "@/pages/checkout/page";
import CheckoutCard from "@/pages/checkout/card/page";
import CheckoutPix from "@/pages/checkout/pix/page";
import CheckoutFinished from "@/pages/checkout/finished/page";
import Index from "@/pages/page";
import UserInfo from "@/pages/checkout/user/page";
import Ticket from "@/pages/ticket/page";
import Faq from "@/pages/faq/page";
import Policy from "@/pages/policy/page";
import Terms from "@/pages/terms/page";
import Receipt from "@/pages/receipt/page";
import Register from "@/pages/register/page";
import RegisterPin from "@/pages/register/pin/page";
import RegisterUser from "@/pages/register/user/page";
import RegisterAddress from "@/pages/register/address/page";
import RegisterVehicle from "@/pages/register/vehicle/page";
import RegisterAccess from "@/pages/register/access/page";
import AuthHome from "@/pages/auth/home/page";
import MyData from "@/pages/auth/myData/page";
import Password from "@/pages/auth/password/page";
import BuyVehicle from "@/pages/auth/buy/page";
import AuthVehicles from "@/pages/auth/vehicles/page";
import AuthCards from "@/pages/auth/cards/page";
import Wallet from "@/pages/auth/wallet/page";
import Recharge from "@/pages/auth/payment/page";
import RechargeCard from "@/pages/auth/payment/card/page";
import RechargePix from "@/pages/auth/payment/pix/page";
import PaymentAddress from "@/pages/auth/payment/address/page";
import ForgotPassword from "@/pages/forgotPassword/page";
import ForgotPasswordPin from "@/pages/forgotPassword/pin/page";
import ForgotPasswordData from "@/pages/forgotPassword/password/page";
import RegisterDevice from "@/pages/registerDevice/page";
import { useEffect } from "react";
import { getData, setCustomer } from "@/redux/ducks/customer";
import Spinner from "@/atomic/atoms/Spinner";
import RechargeCreditCard from "@/pages/auth/payment/creditCard/page";
import RechargeFinished from "@/pages/auth/payment/finished/page";
import ChoosePin from "@/pages/register/choosePin/page";
import RegisterAvulso from "@/pages/register/avulso/page";
import NewCreditCard from "@/pages/auth/payment/newCreditCard/page";
import Notifications from "@/pages/auth/notifications/page";
import HashMonitor from "@/components/HashMonitor";
import WebAccessTokenAuth, {
  isWebAccessTokenAuthPending,
  WEB_ACCESS_TOKEN_AUTH_TIMEOUT_MS,
} from "@/components/WebAccessTokenAuth";

const App = () => {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const handleGetData = (_: boolean) => {
    dispatch(
      setCustomer({
        isLoading: false,
      }),
    );
  };

  useEffect(() => {
    if (customer?.refreshToken) {
      dispatch(
        setCustomer({
          isLoading: true,
        }),
      );
      dispatch(
        getData({
          callback: handleGetData,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.refreshToken]);

  // Fallback: libera o spinner se isLoading ficar travado (ex.: getData sem resposta).
  useEffect(() => {
    if (!customer?.isLoading) {
      return;
    }

    const timer = setTimeout(() => {
      dispatch(setCustomer({ isLoading: false }));
    }, WEB_ACCESS_TOKEN_AUTH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [customer?.isLoading, dispatch]);

  const isAuthenticatingWithToken =
    isWebAccessTokenAuthPending() ||
    (customer?.isLoading && !customer?.refreshToken);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ThemeProvider>
        <WebAccessTokenAuth />
        <HashMonitor />
        <Routes>
          {customer?.refreshToken && (
            <>
              <Route path="/" element={<AuthHome />} />
              <Route path="/mydata" element={<MyData />} />
              <Route path="/password" element={<Password />} />
              <Route path="/buy" element={<BuyVehicle />} />
              <Route path="/myvehicles" element={<AuthVehicles />} />
              <Route path="/mycards" element={<AuthCards />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/ticket" element={<Ticket />} />
              <Route path="/payment" element={<Recharge />} />
              <Route path="/payment/address" element={<PaymentAddress />} />
              <Route path="/payment/card" element={<RechargeCard />} />
              <Route path="/payment/pix" element={<RechargePix />} />
              <Route
                path="/payment/creditCard"
                element={<RechargeCreditCard />}
              />
              <Route path="/payment/finished" element={<RechargeFinished />} />
              <Route path="/newcard" element={<NewCreditCard />} />
              <Route path="/notifications" element={<Notifications />} />
            </>
          )}
          {!customer?.refreshToken && !isAuthenticatingWithToken && (
            <>
              <Route path="/" element={<Index />} />
              <Route path="/ticket/user" element={<User />} />
              <Route path="/ticket/pin" element={<Pin />} />
              <Route path="/home" element={<Home />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/user/:id" element={<UserInfo />} />
              <Route path="/checkout/card" element={<CheckoutCard />} />
              <Route path="/checkout/pix" element={<CheckoutPix />} />
              <Route path="/checkout/finished" element={<CheckoutFinished />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/avulso" element={<RegisterAvulso />} />
              <Route path="/register/choosepin" element={<ChoosePin />} />
              <Route path="/register/pin/:id" element={<RegisterPin />} />
              <Route path="/register/user" element={<RegisterUser />} />
              <Route path="/register/address" element={<RegisterAddress />} />
              <Route path="/register/vehicle" element={<RegisterVehicle />} />
              <Route path="/register/access" element={<RegisterAccess />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/forgot/pin" element={<ForgotPasswordPin />} />
              <Route path="/forgot/password" element={<ForgotPasswordData />} />
              <Route path="/device" element={<RegisterDevice />} />
            </>
          )}
          <Route path="/receipt" element={<Receipt />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {(customer?.isLoading || isAuthenticatingWithToken) && <Spinner />}
      </ThemeProvider>
    </Router>
  );
};

export default App;
